import {
  Badge,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChatIcon from '@material-ui/icons/Chat';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import { format } from 'date-fns';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSound from 'use-sound';
import alertSound from '../../assets/sound.mp3';
import { AuthContext } from '../../context/Auth/AuthContext';
import toastError from '../../errors/toastError';
import { getImageUrl } from '../../helpers/imageHelper';
import useTickets from '../../hooks/useTickets';
import api from '../../services/api';
import openSocket from '../../services/socket-io';
import TicketListItem from '../TicketListItem';

const useStyles = makeStyles((theme) => ({
  tabContainer: {
    overflowY: 'auto',
    maxHeight: 350,
    ...theme.scrollbarStyles,
  },
  popoverPaper: {
    width: 440,
    maxWidth: 'calc(100vw - 24px)',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100vw - 24px)',
    },
  },
}));

const NotificationsPopOver = () => {
  const classes = useStyles();
  const themeStorage = localStorage.getItem('theme');
  const { t } = useTranslation();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const ticketIdUrl = +history.location.pathname.split('/')[2];
  const anchorEl = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isAudioEnabled, setIsAudioEnabled] = useState(
    localStorage.getItem('userAudioEnabled') === 'true'
  );
  const [notificationsAllowed, setNotificationsAllowed] = useState(
    Notification.permission === 'granted'
  );
  const userQueueIds = (user?.queues || []).map((queue) => queue.id);
  const { tickets } = useTickets({
    withUnreadMessages: 'true',
    all: true,
    queueIds: JSON.stringify(userQueueIds),
  });
  const [play, { stop }] = useSound(alertSound, {
    soundEnabled: isAudioEnabled,
  });
  const soundAlertRef = useRef(play);

  useEffect(() => {
    soundAlertRef.current = play;
  }, [play]);

  const requestNotificationPermission = () => {
    if (!('Notification' in window)) {
      alert(t('notifications.unsupported'));
      return;
    }

    Notification.requestPermission()
      .then((permission) => {
        if (permission === 'granted') {
          setNotificationsAllowed(true);
          toast.success(t('notifications.permissionGranted'));
        } else if (permission === 'denied') {
          toast.error(t('notifications.permissionDenied'));
        }
      })
      .catch((error) => {
        console.error('Erro ao solicitar permissão para notificações:', error);
        toast.error(t('notifications.error'));
      });
  };

  const handleNotifications = useCallback(
    (data) => {
      if (!notificationsAllowed) return;

      const { message, contact, ticket } = data;

      const options = {
        body: `${message.body} - ${format(new Date(), 'HH:mm')}`,
        icon: contact.profilePicUrl,
        tag: ticket.id,
        renotify: true,
      };

      const notification = new Notification(
        `${t('tickets.notification.message')} ${contact.name}`,
        options
      );

      notification.onclick = (e) => {
        e.preventDefault();
        window.focus();
        history.push(`/tickets/${ticket.id}`);
      };

      if (isAudioEnabled && soundAlertRef.current) {
        soundAlertRef.current();
      }
    },
    [isAudioEnabled, history, notificationsAllowed, t]
  );

  useEffect(() => {
    const socket = openSocket();
    if (!socket) return undefined;

    const handleConnect = () => socket.emit('joinNotification');
    const canSeeTicket = (ticket) => {
      const userQueues = user?.queues || [];
      const normalizedProfile = (user?.profile || '').toLowerCase();
      const isAdmin =
        normalizedProfile === 'admin' || normalizedProfile === 'masteradmin';
      const belongsToUserQueue = userQueues.some(
        (queue) => Number(queue?.id) === Number(ticket?.queueId)
      );

      return (
        isAdmin ||
        Number(ticket?.userId) === Number(user?.id) ||
        (!ticket?.userId && ticket?.queueId && belongsToUserQueue)
      );
    };

    const handleAppMessage = (data) => {
      if (
        data.action === 'create' &&
        !data.message.read &&
        canSeeTicket(data.ticket)
      ) {
        setNotifications((prevState) => {
          const ticketIndex = prevState.findIndex(
            (t) => t.id === data.ticket.id
          );
          if (ticketIndex !== -1) {
            prevState[ticketIndex] = data.ticket;
            return [...prevState];
          }
          return [data.ticket, ...prevState];
        });

        const shouldNotNotify =
          (data.message.ticketId === ticketIdUrl &&
            document.visibilityState === 'visible') ||
          (data.ticket.userId &&
            Number(data.ticket.userId) !== Number(user?.id));

        if (shouldNotNotify) return;

        handleNotifications(data);
      }
    };

    const handleTicketUpdate = (data) => {
      const ticketId = Number(data.ticketId || data.ticket?.id);

      setNotifications((current) => {
        if (data.action === 'delete') {
          return current.filter((ticket) => Number(ticket.id) !== ticketId);
        }

        if (data.action !== 'update' || !data.ticket) {
          return current;
        }

        const shouldKeep =
          data.ticket.status !== 'closed' &&
          Number(data.ticket.unreadMessages || 0) > 0 &&
          canSeeTicket(data.ticket);

        if (!shouldKeep) {
          return current.filter((ticket) => Number(ticket.id) !== ticketId);
        }

        const exists = current.some((ticket) => Number(ticket.id) === ticketId);
        return exists
          ? current.map((ticket) =>
              Number(ticket.id) === ticketId ? data.ticket : ticket
            )
          : [data.ticket, ...current];
      });
    };

    socket.on('connect', handleConnect);
    socket.on('appMessage', handleAppMessage);
    socket.on('ticket', handleTicketUpdate);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('appMessage', handleAppMessage);
      socket.off('ticket', handleTicketUpdate);
    };
  }, [user, ticketIdUrl, handleNotifications]);

  useEffect(() => {
    setNotifications(tickets);
  }, [tickets]);

  useEffect(() => {
    const updateDocumentTitle = async () => {
      try {
        const { data } = await api.get('/personalizations');
        let baseTitle = 'wbot';
        let faviconUrl =
          'https://github.com/rtenorioh/Press-Ticket/blob/main/frontend/public/favicon.ico?raw=true';
        if (data && data.length > 0) {
          const lightConfig = data.find((config) => config.theme === 'light');
          const darkConfig = data.find((config) => config.theme === 'dark');

          if (themeStorage === 'light' && lightConfig) {
            faviconUrl = lightConfig.favico || faviconUrl;
            baseTitle = lightConfig.company || baseTitle;
          } else if (themeStorage === 'dark' && darkConfig) {
            faviconUrl = darkConfig.favico || faviconUrl;
          }
        }
        document.title =
          notifications.length > 0
            ? `(${notifications.length}) ${baseTitle}`
            : baseTitle;

        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = getImageUrl(faviconUrl);
      } catch (err) {
        toastError(err, t);
        document.title = 'Erro ao carregar título';
      }
    };

    updateDocumentTitle();
  }, [notifications, themeStorage, t]);

  const toggleAudio = () => {
    const newAudioStatus = !isAudioEnabled;
    setIsAudioEnabled(newAudioStatus);
    localStorage.setItem('userAudioEnabled', newAudioStatus.toString());

    if (!newAudioStatus) {
      stop();
    }
  };

  const handleClick = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const NotificationTicket = ({ children }) => (
    <div onClick={handleClickAway}>{children}</div>
  );

  return (
    <>
      <IconButton
        onClick={toggleAudio}
        aria-label="Toggle Sound"
        color="inherit"
      >
        {isAudioEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
      </IconButton>
      {!notificationsAllowed && (
        <IconButton
          onClick={requestNotificationPermission}
          color="inherit"
          variant="contained"
        >
          <Tooltip title={t('notifications.allow')} arrow>
            <HelpOutlineIcon />
          </Tooltip>
        </IconButton>
      )}
      <IconButton
        onClick={handleClick}
        ref={anchorEl}
        aria-label="Open Notifications"
        color="inherit"
      >
        <Badge
          badgeContent={notifications.length}
          color="secondary"
          overlap="rectangular"
          max={999999}
        >
          <ChatIcon />
        </Badge>
      </IconButton>
      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        classes={{ paper: classes.popoverPaper }}
        onClose={handleClickAway}
      >
        <List dense className={classes.tabContainer}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText>{t('notifications.noTickets')}</ListItemText>
            </ListItem>
          ) : (
            notifications.map((ticket) => (
              <NotificationTicket key={ticket.id}>
                <TicketListItem ticket={ticket} />
              </NotificationTicket>
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

export default NotificationsPopOver;
